/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Grid,
  GridItem,
  HStack,
  Text,
  useBoolean,
  VStack,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useParams } from 'react-router-dom';
import CQContainer from '../../components/layout/CQContainer';
import AppColors from '../../constants/AppColors';
import { BackArrowIcon } from '../../constants/IconData';
import ROUTES from '../../constants/Routes';
import { FONT_FAMILY } from '../../constants/Theme';
import accountsRepository from '../../repositories/AccountsRepository';
import { getHeaderCellProps } from '../../utils/assigned_audit.util';
import { replaceRoute, toTitleCase } from '../../utils/common.utils';
import AccountsChartSection from './sections/AccountsChartSection';
import AuditAccuracySection from './sections/AuditAccuracySection';
import MembersSection from './sections/MembersSection';
import MetricSection from './sections/MetricSection';
import StatisticsSection from './sections/StatisticsSection';
import SubCollectionSection from './sections/SubCollectionSection';
import ViewProfileBtn from './components/ViewProfileBtn';

const columnHelper = createColumnHelper();

const HospitalViewContainer = props => {
  const params = useParams();
  const location = useLocation();
  const { accountId, hospitalId } = params;

  const [isFetchingDepartments, ifdState] = useBoolean(false);
  const [pathData, setPathData] = useState(null)

  const [departments, setDepartments] = useState([]);

  const queryParam = useMemo(() => ({ hospital_id: hospitalId }), []);

  async function fetchDepartments(search="") {
    try {
      ifdState.on();
  
      const response = await accountsRepository.getHospitalDepartments(
        {hospital_id: hospitalId,
          search:search,
        }
      );
      
      setDepartments(response);
    } catch (e) {
    } finally {
      ifdState.off();
    }
  }

  useEffect(() => {
    fetchDepartments();
  }, []);

  // add error handling, if the accountId from params, does not match the accountId from the pathData.
  // Show an error page
  async function loadPathData() {
    if (!location.state?.accountName || !location.state?.hospitalName) {
      const res = await accountsRepository.getHospital(hospitalId);
      setPathData({
        accountName: res?.parent_hs?.name || res?.parent_pg?.name,
        hospitalName: res?.name,
     })
   } else {
     setPathData({
       accountName: location.state?.accountName,
       hospitalName: location.state?.hospitalName,
     })
   }
  }

  useEffect(() => {
    loadPathData();
    
  }, [hospitalId]);

  // department columns
  const departmentColumns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: () => 'Department Name',
        enableSorting: false,
      }),
      columnHelper.accessor('active_audits', {
        header: () => 'Active Audits',
        enableSorting: false,
      }),
      columnHelper.accessor('chart_accuracy', {
        header: () => 'Chart Accuracy',
        enableSorting: false,
        cell: info => {
          if (info.row.original.chart_accuracy) {
            return info.row.original.chart_accuracy;
          } else {
            return 'N/A';
          }
        },
      }),
      columnHelper.accessor('cq_rev_opp', {
        header: () => 'RI Rev. Opp.',
        enableSorting: false,
      }),
      columnHelper.accessor('actions', {
        header: () => 'Actions',
        enableSorting: false,
        cell: info => {
          const departmentId = info.row.original.id ?? 1;
          const accountId = params.accountId;
          const hospitalId = params.hospitalId;

          const navigationRoute = replaceRoute(ROUTES.GET_ACCOUNT_DEPARTMENT, {
            hospitalId,
            accountId,
            departmentId,
          });

          return (
              <Link
              to={navigationRoute}
              state={{
                ...pathData,
                departmentName: info.row.original.name,
              }}
            >
              <Button
                size={'md'}
                px={16}
                borderRadius={'full'}
                colorScheme={'secondary'}
              >
                View
              </Button>
            </Link>
          );
        },
      }),
    ];
  }, [pathData]);

  // columns
  const statisticRows = [
    {
      label: 'Total Audits',
      accessor: 'total_audits',
    },
    {
      label: 'In Progress',
      accessor: 'in_progress',
    },
    {
      label: 'Completed',
      accessor: 'completed',
    },
    {
      label: 'Open Rebuttals',
      accessor: 'open_rebuttals',
    },
    {
      label: 'Most used tile',
      accessor: 'most_used_tile'
    }  
  ];

  function _getHeaderCellProps(header) {
    return {
      ...getHeaderCellProps(header),
      justifyContent: header.id === 'name' ? 'flex-start' : 'center',
    };
  }

  function _getCellProps(row, cell) {
    const isName = cell.column.id === 'name';
    const textAlign = isName ? 'left' : 'center';
    const textTransform = isName ? 'capitalize' : 'none';

    return {
      fontSize: 'md',
      textAlign,
      textTransform,
      style: {
        fontWeight: 'bold',
        color: AppColors.secondary,
      },
    };
  }

  function renderBreadCrumb() {
    const accountViewRoute = replaceRoute(ROUTES.GET_ACCOUNT, {
      accountId: accountId,
    });

    return (
      <Breadcrumb mb={5}>
        <BreadcrumbItem>
          <NavLink to={ROUTES.ACCOUNTS_V2}>
            <Text
              fontFamily={FONT_FAMILY.baiJamurjee}
              fontWeight="bold"
              fontSize={'2xl'}
              _hover={{ textDecoration: 'underline' }}
            >
              Accounts
            </Text>
          </NavLink>
        </BreadcrumbItem>

        <BreadcrumbItem>
          <NavLink to={accountViewRoute} state={pathData}>
            <Text 
            fontFamily={FONT_FAMILY.baiJamurjee}
            _hover={{ textDecoration: 'underline' }}
            >
              {toTitleCase(pathData?.accountName)}
            </Text>
          </NavLink>
        </BreadcrumbItem>

        <BreadcrumbItem>
          <Text>{toTitleCase(pathData?.hospitalName)}</Text>
        </BreadcrumbItem>
      </Breadcrumb>
    );
  }

  function renderBackToAccounts() {
    return (
      // Without HStack, this NavLink is occupying entire width
      <HStack
        w={'100%'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <NavLink to={ROUTES.ACCOUNTS_V2}>
          <Box>
            <HStack my={2} spacing={'md'}>
              <BackArrowIcon
                style={{ width: 40, height: 40, cursor: 'pointer' }}
              />
              <Text
                fontSize={'md'}
                fontWeight="bold"
                size={'md'}
                mx={2}
                fontFamily={FONT_FAMILY.openSans}
                color={AppColors.secondary}
              >
                Back to All Accounts
              </Text>
            </HStack>
          </Box>
        </NavLink>
        <ViewProfileBtn queryParam={queryParam} />
      </HStack>
    );
  }

  function renderStatistics() {
    return (
      <GridItem bgColor={'#fff'} p={8} borderRadius={'lg'} shadow={'md'}>
        <StatisticsSection
          rows={statisticRows}
          sectionTitle={'Hospital Statistics'}
          queryParam={queryParam}
        />
      </GridItem>
    );
  }

  function renderAuditAccuracy() {
    return (
      <GridItem bgColor={'#fff'} p={8} borderRadius={'lg'} shadow={'md'}>
        <AuditAccuracySection queryParam={queryParam} />
      </GridItem>
    );
  }

  function renderMetric() {
    return (
      <GridItem bgColor={'#fff'} p={8} borderRadius={'lg'} shadow={'md'}>
        <MetricSection queryParam={queryParam} />
      </GridItem>
    );
  }

  function renderMembers() {
    return (
      <GridItem
        bgColor={'#fff'}
        p={8}
        borderRadius={'lg'}
        shadow={'md'}
      >
        <MembersSection accountId={accountId} queryParam={queryParam} />
      </GridItem>
    );
  }

  function renderDepartments() {
    return (
      <SubCollectionSection
        sectionTitle={'Departments'}
        columns={departmentColumns}
        data={departments}

        isLoading={isFetchingDepartments}
        onSearch={fetchDepartments}
        tableProps={{
          getCellProps: _getCellProps,
          getHeaderCellProps: _getHeaderCellProps,
        }}
      />
    );
  }

  function renderTable() {
    return (
      <GridItem colSpan={2}>
        <VStack align={'stretch'}>
          {renderDepartments()}
          <Box my={4} />
          <AccountsChartSection queryParam={queryParam} />
        </VStack>
      </GridItem>
    );
  }

  function renderMainContent() {
    return (
      <Box>
        <Grid templateColumns="repeat(3, 1fr)" mt={4} gap={4}>
          {renderStatistics()}
          {renderAuditAccuracy()}
          {renderMetric()}
        </Grid>

        <Box my={4} />

        <Grid templateColumns="repeat(3, 1fr)" gap={4}>
          {renderMembers()}
          {renderTable()}
        </Grid>
      </Box>
    );
  }

  return (
    <CQContainer>
      {renderBreadCrumb()}
      {renderBackToAccounts()}
      {renderMainContent()}
    </CQContainer>
  );
};

export default HospitalViewContainer;
